import { useCallback, useEffect, useRef, useState } from "react";
import { type Ponto, limitesDoTraco, recortarNaMoldura } from "../lib/assinaturaCanvas.ts";

/**
 * "Signature pad": o usuário DESENHA a assinatura na tela (dedo/caneta/mouse) e
 * geramos um PNG de **fundo transparente**, recortado à área com tinta, pronto
 * para o pipeline de upload (`useAssinatura.enviar`).
 *
 * Decisões de implementação (frontend senior):
 * - **Pointer Events** unificam toque/caneta/mouse; `setPointerCapture` mantém o
 *   traço mesmo se o dedo sair do quadro. O CSS `touch-action: none` no canvas
 *   impede a página de ROLAR enquanto se assina.
 * - **HiDPI**: o backing store é dimensionado por `devicePixelRatio` e o contexto
 *   é escalado — traço nítido no celular. Os pontos são guardados em coordenadas
 *   CSS (independentes do DPR) para sobreviverem a resize.
 * - **Fundo transparente**: o canvas NUNCA é preenchido (só o wrapper tem fundo
 *   branco, na tela). Logo o `toBlob("image/png")` mantém o alfa.
 * - **Traço suave**: curvas quadráticas por ponto médio entre os pontos.
 * - **Recorte ao traço** antes de exportar: remove as bordas transparentes para a
 *   assinatura escalar bem sobre a linha do relatório.
 */

const COR_TRACO = "#1b2733"; // escuro (lê bem sobre o papel do relatório)
const LARGURA_CSS = 2.4; // espessura do traço em px CSS
const MARGEM_RECORTE = 8; // folga (px CSS) ao redor da tinta no export

type Traco = Ponto[];

export function AssinaturaCanvas({ onConcluir }: { onConcluir: (arquivo: File) => void }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tracosRef = useRef<Traco[]>([]); // todos os traços (para redesenhar/undo/export)
  const tracoAtualRef = useRef<Traco | null>(null);
  const dprRef = useRef(1);
  const [vazio, setVazio] = useState(true);

  /** (Re)configura o canvas ao tamanho atual (DPR) e redesenha os traços. */
  const configurar = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    dprRef.current = dpr;
    const rect = canvas.getBoundingClientRect();
    canvas.width = Math.max(1, Math.round(rect.width * dpr));
    canvas.height = Math.max(1, Math.round(rect.height * dpr));
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.scale(dpr, dpr); // a partir daqui desenhamos em coordenadas CSS
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = COR_TRACO;
    ctx.fillStyle = COR_TRACO;
    ctx.lineWidth = LARGURA_CSS;
    redesenhar();
  }, []);

  useEffect(() => {
    configurar();
    window.addEventListener("resize", configurar);
    return () => window.removeEventListener("resize", configurar);
  }, [configurar]);

  /** Redesenha TODOS os traços (o canvas parte transparente — nunca preenchido). */
  function redesenhar() {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const rect = canvas.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    for (const t of tracosRef.current) {
      desenharTraco(ctx, t);
    }
  }

  function desenharTraco(ctx: CanvasRenderingContext2D, pts: Traco) {
    if (pts.length === 1) {
      const p = pts[0];
      if (!p) return;
      ctx.beginPath();
      ctx.arc(p.x, p.y, LARGURA_CSS / 2, 0, Math.PI * 2);
      ctx.fill();
      return;
    }
    ctx.beginPath();
    const p0 = pts[0];
    if (!p0) return;
    ctx.moveTo(p0.x, p0.y);
    for (let i = 1; i < pts.length - 1; i++) {
      const a = pts[i];
      const b = pts[i + 1];
      if (!a || !b) continue;
      ctx.quadraticCurveTo(a.x, a.y, (a.x + b.x) / 2, (a.y + b.y) / 2); // ponto médio = suavização
    }
    const ultimo = pts[pts.length - 1];
    if (ultimo) ctx.lineTo(ultimo.x, ultimo.y);
    ctx.stroke();
  }

  function pontoDe(e: React.PointerEvent<HTMLCanvasElement>): Ponto {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  const aoBaixar = (e: React.PointerEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    tracoAtualRef.current = [pontoDe(e)];
    tracosRef.current.push(tracoAtualRef.current);
    setVazio(false);
    redesenhar();
  };

  const aoMover = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!tracoAtualRef.current) return;
    e.preventDefault();
    tracoAtualRef.current.push(pontoDe(e));
    redesenhar();
  };

  const aoSoltar = () => {
    tracoAtualRef.current = null;
  };

  const limpar = () => {
    tracosRef.current = [];
    tracoAtualRef.current = null;
    setVazio(true);
    redesenhar();
  };

  const desfazer = () => {
    tracosRef.current.pop();
    setVazio(tracosRef.current.length === 0);
    redesenhar();
  };

  /** Recorta à área com tinta e exporta um PNG transparente → callback. */
  const usar = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pontos = tracosRef.current.flat();
    const lim = limitesDoTraco(pontos, MARGEM_RECORTE);
    if (!lim) return;
    const rect = canvas.getBoundingClientRect();
    const alvo = recortarNaMoldura(lim, rect.width, rect.height); // clamp ao canvas (px CSS)
    const dpr = dprRef.current;
    const saida = document.createElement("canvas");
    saida.width = Math.max(1, Math.round(alvo.largura * dpr));
    saida.height = Math.max(1, Math.round(alvo.altura * dpr));
    const octx = saida.getContext("2d");
    if (!octx) return;
    // Copia só a região com tinta (device px). `saida` nasce transparente → PNG com alfa.
    octx.drawImage(
      canvas,
      alvo.x * dpr,
      alvo.y * dpr,
      alvo.largura * dpr,
      alvo.altura * dpr,
      0,
      0,
      saida.width,
      saida.height,
    );
    saida.toBlob((blob) => {
      if (blob) {
        onConcluir(new File([blob], "assinatura.png", { type: "image/png" }));
      }
    }, "image/png");
  };

  return (
    <div className="m-field">
      <span className="spec" style={{ display: "block", marginBottom: 4 }}>
        Assine no quadro abaixo (vire o celular para mais espaço)
      </span>
      <div
        style={{
          border: "1px dashed var(--border, #cfd8e0)",
          borderRadius: 8,
          background: "#fff",
          overflow: "hidden",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{ display: "block", width: "100%", height: 180, touchAction: "none", cursor: "crosshair" }}
          onPointerDown={aoBaixar}
          onPointerMove={aoMover}
          onPointerUp={aoSoltar}
          onPointerCancel={aoSoltar}
          onPointerLeave={aoSoltar}
        />
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8 }}>
        <button type="button" className="m-btn primary" onClick={usar} disabled={vazio}>
          Usar assinatura
        </button>
        <button type="button" className="m-btn" onClick={desfazer} disabled={vazio}>
          Desfazer
        </button>
        <button type="button" className="m-btn" onClick={limpar} disabled={vazio}>
          Limpar
        </button>
      </div>
    </div>
  );
}
